import { Box, Button, ButtonGroup } from "@mui/material";
import { pxToRem } from "../../utils/fontSizeUtility";
import { WorkspaceTitle } from "./internals/components/WorkspaceTitle";
import { WorkspaceActions } from "./internals/components/WorkspaceActions";
import { WorkspaceContent } from "./internals/components/WorkspaceContent";
import { RelativeBox } from "./internals/components/RelativeBox";
import { CenteredCircularProgress } from "./internals/components/CenteredCircularProgress";
import React, { createContext } from "react";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { ChatBlockTimestampPaper } from "../../platform-style";

export const WorkspaceContext = createContext(false);

interface PlatformDialogProps {
  open?: boolean;
  title: string;
  onClose?: () => void;
  closeLabel?: string;
  onSubmit?: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  children?: React.JSX.Element;
  maxHeight?: number;
  maxWidth?: number;
  destructive?: boolean;
  confirm?: boolean;
  confirmLabel?: string;
  previousButton?: () => void;
  nextButton?: () => void;
  isPreviousButtonDisabled?: boolean;
  isNextButtonDisabled?: boolean;
  submitOnAltEnter?: boolean;
  countOfRecords?: string;
  isDisplayActionButtons?: boolean;
  negativeActionHandler?: () => void;
  workspaceKey?: string;
  optionalControlSlot?: React.JSX.Element;
}

export function Workspace({
  title,
  onClose,
  closeLabel,
  onSubmit,
  submitLabel = "submit",
  isSubmitting = false,
  isSubmitDisabled = false,
  children,
  destructive = false,
  confirm = false,
  confirmLabel = "",
  previousButton,
  nextButton,
  isPreviousButtonDisabled = false,
  isNextButtonDisabled = false,
  submitOnAltEnter = false,
  countOfRecords = "",
  isDisplayActionButtons = true,
  negativeActionHandler,
  workspaceKey = "",
  optionalControlSlot,
}: PlatformDialogProps) {
  const defaultCloseLabel = onSubmit ? "cancel" : "close";
  const closeButtonVariant = onSubmit ? "outlined" : "contained";

  const [isConfirming, setIsConfirming] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (onClose) {
      const closeHandler = (event: Event) => {
        const customEvent = event as CustomEvent<{ key: string }>;
        if (customEvent.detail?.key.trim() == workspaceKey?.trim()) {
          onClose();
        }
        if (customEvent.detail?.key.trim() === "command-workspace" && workspaceKey === "") {
          onClose();
        }
      };
      window.addEventListener("CloseWorkspace", closeHandler as EventListener);
      return () => {
        window.removeEventListener("CloseWorkspace", closeHandler as EventListener);
      };
    }
  }, [onClose]);

  function onConfirm() {
    setIsConfirming(true);
  }

  function onConfirmBack() {
    setIsConfirming(false);
  }

  return (
    <WorkspaceContext.Provider value={true}>
      <Box
        tabIndex={-1}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          borderLeft: "1px solid",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
        onKeyDown={(e) => {
          if (submitOnAltEnter && !isSubmitDisabled) {
            if (e.key === "Enter" && e.altKey) {
              if (onSubmit) {
                onSubmit();
              }
            }
          }
        }}
      >
        <WorkspaceTitle destructive={destructive}>{title}</WorkspaceTitle>
        <WorkspaceContent>{children}</WorkspaceContent>
        <WorkspaceActions
          sx={{
            justifyContent:
              (previousButton && nextButton) || optionalControlSlot ? "space-between" : "flex-end",
          }}
        >
          {/* showing the confirmation after submit if the "confirm" property is set to true  */}
          {isConfirming ? (
            <>
              <Box sx={{ marginRight: pxToRem(16) }}>{isConfirming ? confirmLabel : ""}</Box>
              <Button
                variant={closeButtonVariant}
                onClick={onConfirmBack}
                color={destructive ? "error" : "primary"}
              >
                {"back"}
              </Button>
              {onSubmit ? (
                <RelativeBox>
                  <Button
                    variant="contained"
                    color={destructive ? "error" : isConfirming ? "success" : "primary"}
                    disabled={isSubmitDisabled || isSubmitting}
                    onClick={onSubmit}
                    sx={{ marginLeft: pxToRem(10) }}
                  >
                    {"confirm"}
                  </Button>
                  {isSubmitting && <CenteredCircularProgress size={24} color={"primary"} />}
                </RelativeBox>
              ) : null}
            </>
          ) : (
            <>
              {/* showing the previous and next buttons if properties exist. */}
              {previousButton && nextButton ? (
                <>
                  <ButtonGroup variant="outlined" aria-label="previous next button group">
                    <Button
                      onClick={previousButton}
                      aria-label="previous button"
                      disabled={isPreviousButtonDisabled}
                    >
                      <NavigateBefore sx={{ fontSize: "large" }} />
                    </Button>
                    <Button
                      onClick={nextButton}
                      aria-label="next button"
                      disabled={isNextButtonDisabled}
                    >
                      <NavigateNext sx={{ fontSize: "large" }} />
                    </Button>
                  </ButtonGroup>
                  <ChatBlockTimestampPaper
                    isUser={false}
                    sx={{ background: "none", marginTop: "auto", flexWrap: "nowrap" }}
                  >
                    {countOfRecords}
                  </ChatBlockTimestampPaper>
                </>
              ) : (
                optionalControlSlot
              )}
              {isDisplayActionButtons && (
                <RelativeBox>
                  <Button
                    variant={closeButtonVariant}
                    onClick={negativeActionHandler ? negativeActionHandler : onClose}
                    color={destructive ? "error" : "inherit"}
                  >
                    {closeLabel ? closeLabel : defaultCloseLabel}
                  </Button>

                  {/* showing the submit button */}
                  {onSubmit ? (
                    <>
                      <Button
                        variant="contained"
                        color={destructive ? "error" : isConfirming ? "success" : "primary"}
                        disabled={isSubmitDisabled || isSubmitting}
                        onClick={confirm ? onConfirm : onSubmit}
                        sx={{
                          marginLeft: pxToRem(10),
                        }}
                      >
                        {" "}
                        {submitLabel}
                      </Button>
                      {isSubmitting && <CenteredCircularProgress size={24} color={"primary"} />}
                    </>
                  ) : null}
                </RelativeBox>
              )}
            </>
          )}
        </WorkspaceActions>
      </Box>
    </WorkspaceContext.Provider>
  );
}
